// AddsPageInDetail.jsx
// Uses https://landnest.net:81/ad-properties/combined/

import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Grid, Chip,
  Divider, LinearProgress, Paper, TextField, MenuItem,
  CircularProgress, IconButton,
} from '@mui/material';
import {
  Download, Phone, ChevronRight, LocationOn, CheckCircle,
  Construction, Security, Bolt, Groups, Pool, FitnessCenter,
  SportsEsports, DirectionsRun, ChildCare, Park, SelfImprovement,
  MeetingRoom, Theaters, Work, ElectricCar, LocalParking, Water,
  Videocam, ChildFriendly, Spa, HotTub, SportsTennis,
  SportsBasketball, SportsCricket, SportsSoccer, DirectionsBike,
  LocalLibrary, LocalCafe, Event, Hotel, Texture, Kitchen, Bathtub,
  FormatPaint, Home as HomeIcon, Map, Train, Flight, DirectionsBus,
  School, LocalHospital, ShoppingCart, ApartmentOutlined, Description,
  Gavel, AccountBalance, Star, ArrowBack,
} from '@mui/icons-material';

import { HiOutlinePhoto } from "react-icons/hi2";
import { CgPlayButtonO } from "react-icons/cg";
import { AuthContext } from '../../AuthContext/AuthContext';

/* ------------------------------------------------------------------
   THEME
------------------------------------------------------------------ */
const COLORS = {
  primary: '#8B4A2B',
  primaryDark: '#6B3620',
  primaryLight: '#FBEEE0',
  bg: '#FFFFFF',
  cardBg: '#FFFFFF',
  chipDark: '#3D2B1F',
  green: '#2E7D32',
  greenBg: '#E8F5E9',
  blue: '#1565C0',
  textSecondary: '#8A7A6D',
  textDark: '#2B2118',
  border: '#F0E6DA',
  inputBg: '#FAF5EF',
};

/* ------------------------------------------------------------------
   API CONFIG
------------------------------------------------------------------ */
const API_ORIGIN = 'https://landnest.net:81';
const AD_API_URL = `${API_ORIGIN}/ad-properties/combined/`;

/* ------------------------------------------------------------------
   ICON RESOLUTION HELPERS
------------------------------------------------------------------ */
const ICON_KEYWORDS = [
  { keys: ['security', 'cctv', 'guard', '24'], icon: Security },
  { keys: ['power', 'backup', 'electrical', 'bolt'], icon: Bolt },
  { keys: ['club', 'community'], icon: Groups },
  { keys: ['swim', 'pool'], icon: Pool },
  { keys: ['gym', 'fitness'], icon: FitnessCenter },
  { keys: ['indoor', 'game', 'sports esports'], icon: SportsEsports },
  { keys: ['jog', 'run', 'track'], icon: DirectionsRun },
  { keys: ['child', 'kid', 'play'], icon: ChildCare },
  { keys: ['senior', 'park', 'garden', 'landscape'], icon: Park },
  { keys: ['yoga', 'meditation'], icon: SelfImprovement },
  { keys: ['multipurpose', 'hall', 'banquet', 'party'], icon: MeetingRoom },
  { keys: ['theatre', 'theater', 'cinema'], icon: Theaters },
  { keys: ['co-work', 'cowork', 'office', 'business', 'lounge'], icon: Work },
  { keys: ['ev', 'electric', 'charging'], icon: ElectricCar },
  { keys: ['visitor', 'parking'], icon: LocalParking },
  { keys: ['rain', 'water', 'harvest'], icon: Water },
  { keys: ['cctv', 'camera', 'surveillance'], icon: Videocam },
  { keys: ['kids pool', 'child pool'], icon: ChildFriendly },
  { keys: ['spa'], icon: Spa },
  { keys: ['sauna', 'hot tub', 'jacuzzi'], icon: HotTub },
  { keys: ['badminton', 'tennis'], icon: SportsTennis },
  { keys: ['basketball'], icon: SportsBasketball },
  { keys: ['cricket', 'net'], icon: SportsCricket },
  { keys: ['football', 'soccer', 'turf'], icon: SportsSoccer },
  { keys: ['cycling', 'bike'], icon: DirectionsBike },
  { keys: ['library', 'reading'], icon: LocalLibrary },
  { keys: ['cafe', 'coffee'], icon: LocalCafe },
  { keys: ['event', 'auditorium'], icon: Event },
  { keys: ['hotel', 'guest', 'room'], icon: Hotel },
  { keys: ['flooring', 'floor', 'texture'], icon: Texture },
  { keys: ['kitchen', 'modular'], icon: Kitchen },
  { keys: ['bathroom', 'bath', 'toilet'], icon: Bathtub },
  { keys: ['painting', 'paint'], icon: FormatPaint },
  { keys: ['smart', 'home', 'automation'], icon: HomeIcon },
  { keys: ['door', 'window'], icon: MeetingRoom },
  { keys: ['metro', 'train', 'railway'], icon: Train },
  { keys: ['airport', 'flight'], icon: Flight },
  { keys: ['bus', 'transit'], icon: DirectionsBus },
  { keys: ['school', 'college', 'education'], icon: School },
  { keys: ['hospital', 'clinic', 'medical'], icon: LocalHospital },
  { keys: ['mall', 'shopping', 'market'], icon: ShoppingCart },
  { keys: ['it park', 'tech', 'office park'], icon: ApartmentOutlined },
  { keys: ['brochure', 'document', 'pdf'], icon: Description },
  { keys: ['legal', 'rera', 'approval'], icon: Gavel },
  { keys: ['bank', 'loan', 'finance'], icon: AccountBalance },
];

const resolveIcon = (label = '') => {
  const lower = String(label).toLowerCase();
  for (const { keys, icon } of ICON_KEYWORDS) {
    if (keys.some((k) => lower.includes(k))) return icon;
  }
  return CheckCircle;
};

/* ------------------------------------------------------------------
   FORMATTERS
------------------------------------------------------------------ */
const formatPrice = (price) => {
  if (!price && price !== 0) return 'Price on Request';
  if (price >= 10000000) return `₹${(price / 10000000).toFixed(price % 10000000 === 0 ? 0 : 2)}Cr`;
  if (price >= 100000) return `₹${(price / 100000).toFixed(price % 100000 === 0 ? 0 : 1)}L`;
  return `₹${Number(price).toLocaleString()}`;
};

const formatPriceLong = (price) => {
  if (price === undefined || price === null) return 'Price on Request';
  if (price >= 10000000) return `₹${parseFloat((price / 10000000).toFixed(2))} Crores`;
  if (price >= 100000) return `₹${parseFloat((price / 100000).toFixed(2))} Lakhs`;
  return `₹${Number(price).toLocaleString()}`;
};

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '';

const prettyBhk = (raw) => {
  if (!raw) return 'Unit';
  return String(raw).replace(/(\d+\+?)\s*BHK/i, '$1 BHK').trim();
};

const BHK_SORT = (a, b) => {
  const num = (s) => {
    const m = String(s || '').match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 999;
  };
  return num(a) - num(b);
};

const isEmpty = (v) =>
  v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

const show = (v) => (isEmpty(v) ? '—' : String(v));

const withUnit = (v, unit) => {
  if (isEmpty(v)) return '';
  return /^[\d.,]+$/.test(String(v).trim()) ? `${v} ${unit}` : String(v);
};

const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (typeof imagePath !== 'string') return '';
  if (imagePath.startsWith('http') || imagePath.startsWith('data:')) return imagePath;
  return `${API_ORIGIN}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
};

/* ------------------------------------------------------------------
   ADAPTERS
------------------------------------------------------------------ */
const labelOf = (item) => {
  if (item === undefined || item === null) return '';
  if (typeof item !== 'object') return String(item);
  return String(item.name || item.title || item.label || '');
};

const EMPTY_DATA = {
  project: {},
  configs: [],
  highlights: [],
  amenities: [],
  specifications: [],
  locationAdvantages: [],
  legal: {},
  brochures: [],
  media: { photos: 0, videos: [], video: '', tour: '' },
};

const normalizeAdProperty = (p) => {
  const property = p || {};

  const configs = (property.configurations || [])
    .slice()
    .sort((a, b) => BHK_SORT(a.bhk_type, b.bhk_type))
    .map((c, i) => ({
      id: c.id ?? i,
      bhk_type: prettyBhk(c.bhk_type),
      price: c.price,
      carpet_area: c.carpet_area,
      built_up_area: c.built_up_area,
      super_built_up_area: c.super_built_up_area,
      bathrooms: c.bathrooms,
      facing: c.facing,
      parking: c.parking,
      balcony: c.balcony,
      images: (c.floor_plan_images || []).map((im) => getImageUrl(im.image)).filter(Boolean),
      brochure: getImageUrl((c.floor_plan_images || [])[0]?.image || ''),
    }));

  const highlights = (property.property_highlights || []).map(labelOf).filter(Boolean);
  const amenities = (property.property_amenities || []).map(labelOf).filter(Boolean);

  const specifications = (property.specifications || [])
    .map((s) => ({ name: s.name || '', value: s.value || '' }))
    .filter((s) => s.name);

  const locationAdvantages = (property.location_advantages || [])
    .map((l) => ({
      place_name: l.place_name || '',
      distance: withUnit(l.distance, 'km'),
      travel_time: withUnit(l.travel_time, 'min'),
    }))
    .filter((l) => l.place_name);

  const brochures = (property.brochures || []).map((doc, i) => ({
    id: doc.id ?? i,
    title: doc.title || 'Document',
    url: getImageUrl(doc.document),
    uploadedAt: doc.uploaded_at ? formatDate(doc.uploaded_at) : '',
  }));

  const legal = {
    bankApproved: property.bank_approved || '',
    approvalStatus: property.approval_status || (property.status ? 'Approved' : ''),
    governmentApprovals: property.government_approvals || '',
    legalClearances: property.legal_clearances || (brochures.length > 0 ? 'All Clear' : ''),
  };

  const cfgPrices = configs.map((c) => c.price).filter((n) => n !== undefined && n !== null);
  const priceRange =
    property.min_budget && property.max_budget
      ? property.min_budget === property.max_budget
        ? formatPrice(property.min_budget)
        : `${formatPrice(property.min_budget)} - ${formatPrice(property.max_budget)}`
      : cfgPrices.length
        ? (Math.min(...cfgPrices) === Math.max(...cfgPrices)
          ? formatPrice(Math.min(...cfgPrices))
          : `${formatPrice(Math.min(...cfgPrices))} - ${formatPrice(Math.max(...cfgPrices))}`)
        : '';

  const heroImages = Array.isArray(property.hero_images) ? property.hero_images : [];
  const sortedHeroImages = heroImages
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((im) => getImageUrl(im.image))
    .filter(Boolean);

  const rawVideos =
    (Array.isArray(property.videos) && property.videos) ||
    (Array.isArray(property.video_urls) && property.video_urls) ||
    (Array.isArray(property.hero_videos) && property.hero_videos) ||
    [];

  let sortedVideos = rawVideos
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((v) => getImageUrl(v?.video || v?.url || v?.video_url || v?.file || v))
    .filter(Boolean);

  if (sortedVideos.length === 0) {
    const single = getImageUrl(property.video || property.video_url || '');
    if (single) sortedVideos = [single];
  }

  const project = {
    builder: property.builder_details?.builder_name || '',
    name: property.property_name || '',
    location: [property.address, property.city].filter(Boolean).join(', '),
    lat: property.lat,
    lng: property.long,
    priceRange,
    bhkTypes: configs.map((c) => c.bhk_type).join(', '),
    status: property.possession_status ? 'Active' : '',
    possession: formatDate(property.possession_date),
    towers: property.towers || property.total_towers || '',
    floors: property.floors || property.overall_floors || '',
    units: property.units || property.total_units || '',
    totalArea: withUnit(property.site_area || property.project_area, 'sq.ft'),
    constructionProgress: Number(property.construction_progress) || 0,
    rera: property.rera_number ? 'RERA APPROVED' : '',
    verified: !!property.status,
    propertyType: property.property_type || '',
    category: property.category_name || '',
    description: property.description || '',
    phone: property.user_mobile_no || '',
    cover: sortedHeroImages[0] || '',
    covers: sortedHeroImages,
    videos: sortedVideos,
  };

  return {
    project,
    configs,
    highlights,
    amenities,
    specifications,
    locationAdvantages,
    legal,
    brochures,
    media: {
      photos: heroImages.length,
      videos: sortedVideos,
      video: sortedVideos[0] || '',
      tour: getImageUrl(property.virtual_tour || property.virtual_tour_url || ''),
    },
  };
};

/* ------------------------------------------------------------------
   Reusable
------------------------------------------------------------------ */
const IconGrid = ({ items, emptyText }) => {
  if (!items || items.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: COLORS.textSecondary, textAlign: 'center', py: 1 }}>
        {emptyText || 'Not available'}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
      {items.map((item) => {
        const IconComp = resolveIcon(item);
        return (
          <Box
            key={item}
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
              gap: 0.5, p: 1, borderRadius: '10px', border: `1px solid ${COLORS.border}`,
              backgroundColor: COLORS.cardBg, minHeight: '60px', justifyContent: 'center', overflow: 'hidden',
            }}
          >
            <IconComp sx={{ fontSize: 24, color: COLORS.primary }} />
            <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: COLORS.textDark, lineHeight: 1.2, wordBreak: 'break-word' }}>
              {item}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const SectionHeaderWithViewAll = ({ title, onViewAll, expanded }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography variant="subtitle1" fontWeight="bold" sx={{ color: COLORS.textDark }}>{title}</Typography>
    {onViewAll && (
      <Typography
        variant="body2"
        onClick={onViewAll}
        sx={{ color: COLORS.blue, fontWeight: 600, cursor: 'pointer' }}
      >
        {expanded ? 'Show Less' : 'View All'}
      </Typography>
    )}
  </Box>
);

const EmptyText = ({ children }) => (
  <Typography variant="body2" sx={{ color: COLORS.textSecondary, textAlign: 'center', py: 3 }}>
    {children}
  </Typography>
);

/* ================================================================== */
const AddsPageInDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userId } = useContext(AuthContext);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [activeTab, setActiveTab] = useState(0);
  const [showAllHighlights, setShowAllHighlights] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);

  const [heroIndex, setHeroIndex] = useState(0);
  const heroScrollRef = useRef(null);
  const heroImagesLengthRef = useRef(0);

  const [userLoading, setUserLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [contactForm, setContactForm] = useState({
    name: '', phone: '', email: '',
    preferredTime: 'morning', message: '',
  });

  /* ---- Fetch user details for prefill ---- */
  useEffect(() => {
    if (!userId) return;
    const fetchUser = async () => {
      try {
        setUserLoading(true);
        const res = await fetch(`${API_ORIGIN}/users/${userId}/`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error('Failed to fetch user');
        const user = await res.json();
        setContactForm((prev) => ({
          ...prev,
          name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || '',
          phone: user.mobile_no || '',
          email: user.email || '',
        }));
      } catch (e) {
        console.error('User fetch error:', e);
      } finally {
        setUserLoading(false);
      }
    };
    fetchUser();
  }, [userId]);

  /* ---- Fetch ad property by id ---- */
  useEffect(() => {
    if (!id) {
      setError('No property id in the URL');
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(`${AD_API_URL}${id}/`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        const json = await res.json();

        let property = json?.data ?? json;
        if (Array.isArray(property)) {
          property = property.find((it) => String(it.property_id) === String(id)) || property[0];
        }

        setData(normalizeAdProperty(property));
        setActiveTab(0);
        setHeroIndex(0);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Something went wrong');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [id, reloadKey]);

  /* ---- Pause non-active videos ---- */
  useEffect(() => {
    const container = heroScrollRef.current;
    if (!container) return;
    const items = container.querySelectorAll('[data-media-type]');
    items.forEach((el) => {
      const type = el.getAttribute('data-media-type');
      const idx = Number(el.getAttribute('data-media-index'));
      if (type === 'video' && idx !== heroIndex) {
        const vid = el.querySelector('video');
        if (vid) { try { vid.pause(); } catch (e) {} }
      }
    });
  }, [heroIndex]);

  /* ---- Loading / Error ---- */
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: COLORS.bg }}>
        <CircularProgress sx={{ color: COLORS.primary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, px: 3, textAlign: 'center', backgroundColor: COLORS.bg }}>
        <Typography sx={{ color: COLORS.textDark, fontWeight: 700 }}>Couldn't load this property</Typography>
        <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>{error}</Typography>
        <Button
          variant="contained"
          onClick={() => setReloadKey((k) => k + 1)}
          sx={{ borderRadius: 2, bgcolor: COLORS.primary, textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: COLORS.primaryDark } }}
        >
          Try again
        </Button>
      </Box>
    );
  }

  const d = data || EMPTY_DATA;
  const { project, configs, media, brochures } = d;
  const activeConfig = configs[activeTab] || null;

  const openUrl = (url) => { if (url) window.open(url, '_blank', 'noopener'); };

  const openGoogleMaps = () => {
    const query = project.lat !== undefined && project.lng !== undefined
      ? `${project.lat},${project.lng}`
      : project.location;
    window.open(`https://www.google.com/maps?q=${encodeURIComponent(query)}`, '_blank');
  };

  const handleContactSubmit = async () => {
    if (!userId) { alert('Please log in to request a callback.'); return; }
    if (!contactForm.name || !contactForm.phone || !contactForm.email) {
      alert('Please fill name, phone, and email.'); return;
    }
    try {
      setSubmitting(true);
      const payload = {
        user_id: Number(userId),
        property: Number(id),
        name: contactForm.name,
        phone: contactForm.phone,
        email: contactForm.email,
        preferred_time: contactForm.preferredTime,
        message: contactForm.message,
      };
      const res = await fetch(`${API_ORIGIN}/request-callback/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result?.message || result?.detail || 'Failed to request callback');
      alert('Callback requested successfully!');
      setContactForm((prev) => ({ ...prev, preferredTime: 'morning', message: '' }));
    } catch (e) {
      console.error(e);
      alert(e.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleHeroScroll = (e) => {
    const el = e.currentTarget;
    const raw = Math.round(el.scrollLeft / el.clientWidth);
    const clamped = Math.max(0, Math.min(raw, heroMedia.length - 1));
    if (clamped !== heroIndex) setHeroIndex(clamped);
  };

  const scrollHeroTo = (index) => {
    const el = heroScrollRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' });
  };

  const visibleHighlights = showAllHighlights ? d.highlights : d.highlights.slice(0, 8);
  const visibleAmenities = showAllAmenities ? d.amenities : d.amenities.slice(0, 8);

  const heroImages = project.covers || [];
  const heroVideos = project.videos || [];
  const heroMedia = [
    ...heroImages.map((src) => ({ type: 'image', src })),
    ...heroVideos.map((src) => ({ type: 'video', src })),
  ];
  heroImagesLengthRef.current = heroImages.length;

  const hasMultipleMedia = heroMedia.length > 1;
  const firstVideoIndex = heroImages.length;
  const currentMedia = heroMedia[heroIndex];
  const currentThumb =
    (currentMedia?.type === 'image' && currentMedia.src) ||
    heroImages[0] ||
    project.cover ||
    '';

  return (
    <Box className="addspageindetail" sx={{ backgroundColor: COLORS.bg, minHeight: '100vh', pb: 10, fontFamily: 'sans-serif', overflowY: 'auto' }}>

      {/* ---------- HERO ---------- */}
      <Box sx={{ position: 'relative', width: '100%', height: 320, mb: 1 }}>
        {heroMedia.length > 0 ? (
          <>
            <Box
              ref={heroScrollRef}
              onScroll={handleHeroScroll}
              sx={{
                display: 'flex', width: '100%', height: '100%',
                overflowX: 'auto', overflowY: 'hidden',
                scrollSnapType: 'x mandatory', scrollBehavior: 'smooth',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { display: 'none' },
                msOverflowStyle: 'none', scrollbarWidth: 'none',
              }}
            >
              {heroMedia.map((item, i) => (
                <Box
                  key={`${item.type}-${i}`}
                  data-media-type={item.type}
                  data-media-index={i}
                  sx={{
                    flex: '0 0 100%', width: '100%', height: '100%',
                    scrollSnapAlign: 'start',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: '#000',
                  }}
                >
                  {item.type === 'image' ? (
                    <Box
                      component="img"
                      src={item.src}
                      alt={`${project.name} ${i + 1}`}
                      draggable={false}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', userSelect: 'none' }}
                    />
                  ) : (
                    <Box
                      component="video"
                      src={item.src}
                      controls
                      playsInline
                      preload="metadata"
                      sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', bgcolor: '#000' }}
                    />
                  )}
                </Box>
              ))}
            </Box>

            {hasMultipleMedia && (
              <Box sx={{
                position: 'absolute', bottom: 44, left: '50%',
                transform: 'translateX(-50%)', display: 'flex', gap: '5px',
                px: 1, py: 0.5, borderRadius: '10px',
                bgcolor: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)', zIndex: 3,
              }}>
                {heroMedia.map((item, i) => (
                  <Box
                    key={i}
                    onClick={() => scrollHeroTo(i)}
                    sx={{
                      width: item.type === 'video' ? 10 : 6,
                      height: 6,
                      borderRadius: item.type === 'video' ? '3px' : '50%',
                      cursor: 'pointer',
                      bgcolor: heroIndex === i ? '#FFFFFF' : 'rgba(255,255,255,0.5)',
                    }}
                  />
                ))}
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ width: '100%', height: '100%', bgcolor: '#E0E0E0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Construction sx={{ fontSize: 60, color: '#BDBDBD' }} />
          </Box>
        )}

        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            position: 'absolute', top: 16, left: 16,
            bgcolor: 'rgba(255,255,255,0.85)', color: COLORS.textDark,
            backdropFilter: 'blur(4px)', '&:hover': { bgcolor: 'white' },
            boxShadow: 1, zIndex: 4,
          }}
        >
          <ArrowBack />
        </IconButton>

        <Box sx={{
          position: 'absolute', bottom: 4, left: 16, right: 16,
          display: 'flex', gap: 1.5, overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' }, pb: 0.5, zIndex: 4,
        }}>
          <Chip
            icon={<HiOutlinePhoto size={16} />}
            label={media.photos ? `${media.photos} Photos` : 'Photos'}
            onClick={() => scrollHeroTo(0)}
            sx={{
              bgcolor: 'rgba(61, 43, 31, 0.85)', color: 'white',
              fontWeight: 600, fontSize: '12px', borderRadius: 10, px: 1,
              backdropFilter: 'blur(4px)', cursor: 'pointer',
              '& .MuiChip-icon': { color: 'white' },
            }}
          />
          {heroVideos.length > 0 && (
            <Chip
              icon={<CgPlayButtonO size={16} />}
              label={heroVideos.length > 1 ? `${heroVideos.length} Videos` : 'Video'}
              onClick={() => scrollHeroTo(firstVideoIndex)}
              sx={{
                bgcolor: heroIndex >= firstVideoIndex ? 'rgba(139, 74, 43, 0.95)' : 'rgba(61, 43, 31, 0.85)',
                color: 'white', fontWeight: 600, fontSize: '12px', borderRadius: 10, px: 1,
                backdropFilter: 'blur(4px)', cursor: 'pointer',
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          )}
        </Box>
      </Box>

      {/* ---------- HEADER CARD ---------- */}
      <Box sx={{ px: 2 }}>
        <Card sx={{ borderRadius: 3, p: 2, boxShadow: '0 4px 20px rgba(139,74,43,0.08)', border: `1px solid ${COLORS.border}` }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ width: 80, height: 100, bgcolor: '#F5F5F5', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
              {currentThumb ? (
                <Box component="img" src={currentThumb} alt={project.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Construction sx={{ fontSize: 40, color: '#D4A373' }} />
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '14px', color: COLORS.primary, fontWeight: 700, mb: 0.5 }}>
                {[project.builder, project.category].filter(Boolean).join(' • ')}
              </Typography>
              <Typography sx={{ fontSize: '18px', fontWeight: 800, color: COLORS.textDark, mb: 0.5, lineHeight: 1.2 }}>
                {project.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <LocationOn sx={{ fontSize: 14, color: COLORS.textSecondary }} />
                <Typography sx={{ fontSize: '12px', color: COLORS.textSecondary }}>
                  {project.location}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {project.rera && (
                  <Chip label={project.rera} size="small" sx={{ bgcolor: COLORS.primaryDark, color: 'white', fontSize: '10px', fontWeight: 700, height: 22, borderRadius: 10 }} />
                )}
                {project.verified && (
                  <Chip icon={<CheckCircle sx={{ fontSize: 12 }} />} label="Verified" size="small" sx={{ bgcolor: COLORS.greenBg, color: COLORS.green, fontSize: '10px', fontWeight: 700, height: 22, borderRadius: 10, '& .MuiChip-icon': { color: COLORS.green } }} />
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2, borderColor: COLORS.border, borderStyle: 'dashed' }} />

          <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Typography sx={{ fontSize: '13px', color: '#4B5563' }}>
              <Box component="span" sx={{ fontWeight: 700, color: '#111827', mr: 0.5 }}>Possession:</Box>
              {show(project.possession)}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#4B5563' }}>
              <Box component="span" sx={{ fontWeight: 700, color: '#111827', mr: 0.5 }}>Type:</Box>
              {show(project.propertyType)}
            </Typography>
          </Box>
        </Card>
      </Box>

      {/* ---------- PRICE ---------- */}
      <Box sx={{ px: 2, mt: 1 }}>
        <Typography sx={{ fontSize: '28px', fontWeight: 800, color: '#6B3620', letterSpacing: '-0.5px' }}>
          {project.priceRange || 'Price on Request'}
        </Typography>
        <Typography sx={{ fontSize: '12px', color: COLORS.textSecondary, letterSpacing: '1px', fontWeight: 600 }}>
          PRICE RANGE
        </Typography>
        {(project.bhkTypes || project.propertyType) && (
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#6B3620' }}>
            {[project.bhkTypes, project.propertyType].filter(Boolean).join(' ')}
          </Typography>
        )}
        {project.description && (
          <Typography sx={{ fontSize: '13px', color: COLORS.textSecondary, mt: 1 }}>
            {project.description}
          </Typography>
        )}
      </Box>

      {/* ---------- STATS ---------- */}
      <Box sx={{ px: 2, mt: 1 }}>
        <Card sx={{ borderRadius: 3, p: 0.5, boxShadow: 'none', border: `1px solid ${COLORS.border}`, bgcolor: '#FAFAFA' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 2, columnGap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: '11px', color: COLORS.textSecondary, mb: 0.5 }}>STATUS</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Construction sx={{ fontSize: 16, color: COLORS.primary }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: COLORS.primary }}>{show(project.status)}</Typography>
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '11px', color: COLORS.textSecondary, mb: 0.5 }}>POSSESSION</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: COLORS.primary }}>{show(project.possession)}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '11px', color: COLORS.textSecondary, mb: 0.5 }}>TOWERS</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: COLORS.primary }}>{show(project.towers)}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '11px', color: COLORS.textSecondary, mb: 0.5 }}>FLOORS</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: COLORS.primary }}>{show(project.floors)}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '11px', color: COLORS.textSecondary, mb: 0.5 }}>UNITS</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: COLORS.primary }}>{show(project.units)}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '11px', color: COLORS.textSecondary, mb: 0.5 }}>TOTAL AREA</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: COLORS.primary }}>{show(project.totalArea)}</Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: COLORS.textDark }}>Construction Progress</Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: COLORS.textDark }}>{project.constructionProgress}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={project.constructionProgress}
              sx={{
                height: 8, borderRadius: 4, bgcolor: '#E0E0E0',
                '& .MuiLinearProgress-bar': { bgcolor: COLORS.primaryDark, borderRadius: 4 },
              }}
            />
          </Box>
        </Card>
      </Box>

      {/* ---------- BHK TABS ---------- */}
      {configs.length > 0 && (
        <Box sx={{ mt: 1, px: 1, bgcolor: 'white' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {configs.map((cfg, idx) => {
              const isActive = activeTab === idx;
              return (
                <Box
                  key={cfg.id}
                  onClick={() => setActiveTab(idx)}
                  sx={{
                    flex: 1, textAlign: 'center', py: 1, borderRadius: 2,
                    border: `1px solid ${isActive ? COLORS.primaryDark : COLORS.border}`,
                    bgcolor: isActive ? COLORS.primaryDark : 'white',
                    color: isActive ? 'white' : COLORS.textDark,
                    fontWeight: 700, fontSize: '12.5px',
                    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                  }}
                >
                  {cfg.bhk_type}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ---------- FLOOR PLANS ---------- */}
      <Box sx={{ p: 1 }}>
        <Typography sx={{ fontSize: '20px', fontWeight: 800, color: COLORS.textDark }}>
          Floor Plans & Pricing
        </Typography>

        {!activeConfig && <EmptyText>No configurations available for this property</EmptyText>}

        {activeConfig && (
          <Card sx={{ mb: 1, borderRadius: 3, border: `1px solid ${COLORS.border}` }}>
            <CardContent sx={{ p: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#6B3620' }}>{activeConfig.bhk_type}</Typography>
                <Box sx={{ bgcolor: COLORS.primaryLight, color: COLORS.primary, fontWeight: 700, fontSize: '11px', px: 1, py: 0.5, borderRadius: 1.5 }}>
                  {activeConfig.price !== undefined ? `Starting at ${formatPrice(activeConfig.price)}` : 'Price on Request'}
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 1.75, columnGap: 2 }}>
                {[
                  { label: 'Carpet Area', value: show(withUnit(activeConfig.carpet_area, 'sqft')) },
                  { label: 'Built-up Area', value: show(withUnit(activeConfig.built_up_area, 'sqft')) },
                  { label: 'Super Built-up', value: show(withUnit(activeConfig.super_built_up_area, 'sqft')) },
                  { label: 'Price', value: formatPriceLong(activeConfig.price) },
                  { label: 'Bathrooms', value: show(activeConfig.bathrooms) },
                  { label: 'Facing', value: show(activeConfig.facing) },
                  { label: 'Parking', value: show(activeConfig.parking) },
                  { label: 'Balcony', value: show(activeConfig.balcony) },
                ].map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '12px', color: COLORS.textSecondary }}>{item.label}</Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: COLORS.textDark }}>{item.value}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{
                display: 'flex', gap: 1.5, overflowX: 'auto', mt: 1, pb: 0.5,
                scrollSnapType: 'x mandatory',
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: COLORS.border, borderRadius: 2 },
              }}>
                {(activeConfig.images.length > 0 ? activeConfig.images : [null, null]).map((src, n) => (
                  <Box
                    key={n}
                    sx={{
                      flex: '0 0 calc(50% - 6px)', minWidth: 'calc(50% - 6px)',
                      height: 150, borderRadius: 2, overflow: 'hidden',
                      border: `1px solid ${COLORS.border}`, bgcolor: '#F5F5F5',
                      scrollSnapAlign: 'start',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {src ? (
                      <Box
                        component="img"
                        src={src}
                        alt={`${activeConfig.bhk_type} floor plan ${n + 1}`}
                        onClick={() => openUrl(src)}
                        sx={{ width: '100%', height: '100%', objectFit: 'contain', cursor: 'pointer' }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '80%', height: '80%' }}>
                        <Box sx={{ display: 'flex', gap: 0.75, height: '40%' }}>
                          <Box sx={{ flex: 1, border: '1px solid #CCC', bgcolor: '#EAEAEA' }} />
                          <Box sx={{ flex: 2, border: '1px solid #CCC', bgcolor: '#EAEAEA' }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.75, height: '60%' }}>
                          <Box sx={{ flex: 2, border: '1px solid #CCC', bgcolor: '#EAEAEA' }} />
                          <Box sx={{ flex: 1, border: '1px solid #CCC', bgcolor: '#EAEAEA' }} />
                        </Box>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                <Button
                  variant="outlined" size="small" startIcon={<Download sx={{ fontSize: 16 }} />}
                  onClick={() => openUrl(activeConfig.brochure || brochures[0]?.url)}
                  disabled={!activeConfig.brochure && !brochures[0]?.url}
                  sx={{
                    flex: 1, borderRadius: 2, color: COLORS.primaryDark,
                    borderColor: COLORS.border, fontWeight: 700, fontSize: '12px',
                    textTransform: 'none',
                    '&:hover': { borderColor: COLORS.primaryDark, bgcolor: COLORS.primaryLight },
                  }}
                >
                  Download
                </Button>
                <Button
                  variant="contained" size="small" startIcon={<Phone sx={{ fontSize: 16 }} />}
                  onClick={() => document.getElementById('ads-request-callback')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                  sx={{
                    flex: 1, borderRadius: 2, bgcolor: COLORS.primaryDark, color: 'white',
                    fontWeight: 700, fontSize: '12px', textTransform: 'none',
                    '&:hover': { bgcolor: COLORS.primary },
                  }}
                >
                  Request Callback
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {configs.map((config, idx) =>
          idx === activeTab ? null : (
            <Paper
              key={config.id}
              onClick={() => setActiveTab(idx)}
              sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                p: 1, borderRadius: 3, cursor: 'pointer',
                border: `1px solid ${COLORS.border}`, boxShadow: 'none',
                bgcolor: 'white', '&:hover': { bgcolor: '#FAFAFA' },
              }}
            >
              <Typography sx={{ fontSize: '15px', fontWeight: 700, color: COLORS.textDark }}>{config.bhk_type}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: COLORS.primary }}>
                  {config.price !== undefined ? `Starting at ${formatPrice(config.price)}` : 'Price on Request'}
                </Typography>
                <ChevronRight sx={{ color: COLORS.textSecondary, fontSize: 20 }} />
              </Box>
            </Paper>
          )
        )}
      </Box>

      {/* ---------- HIGHLIGHTS ---------- */}
      <Box sx={{ px: 0.5 }}>
        <Divider sx={{ borderColor: COLORS.border, mb: 0.5 }} />
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 10px rgba(139,74,43,0.06)', border: `1px solid ${COLORS.border}` }}>
          <CardContent sx={{ p: 0.5 }}>
            <SectionHeaderWithViewAll
              title="Property Highlights"
              onViewAll={d.highlights.length > 8 ? () => setShowAllHighlights(!showAllHighlights) : null}
              expanded={showAllHighlights}
            />
            <IconGrid items={visibleHighlights} emptyText="No highlights available for this property" />
          </CardContent>
        </Card>
      </Box>

      {/* ---------- AMENITIES ---------- */}
      <Box sx={{ px: 0.5 }}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 10px rgba(139,74,43,0.06)', border: `1px solid ${COLORS.border}` }}>
          <CardContent sx={{ p: 0.5 }}>
            <SectionHeaderWithViewAll
              title="Amenities"
              onViewAll={d.amenities.length > 8 ? () => setShowAllAmenities(!showAllAmenities) : null}
              expanded={showAllAmenities}
            />
            <IconGrid items={visibleAmenities} emptyText="No amenities listed for this property" />
          </CardContent>
        </Card>
      </Box>

      {/* ---------- SPECIFICATIONS ---------- */}
      <Box sx={{ px: 0.5 }}>
        <Card sx={{ borderRadius: 3, border: `1px solid ${COLORS.border}` }}>
          <CardContent sx={{ p: 0.5 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: COLORS.textDark }}>Specifications</Typography>
            {d.specifications.length === 0 && <EmptyText>No specifications listed</EmptyText>}
            {d.specifications.map((spec, idx, arr) => {
              const IconComp = resolveIcon(spec.name);
              return (
                <Box
                  key={`${spec.name}-${idx}`}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    py: 0.5,
                    borderBottom: idx < arr.length - 1 ? `1px solid ${COLORS.border}` : 'none',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconComp sx={{ fontSize: 20, color: COLORS.primary }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ color: COLORS.textDark }}>{spec.name}</Typography>
                      <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>{spec.value}</Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      </Box>

      {/* ---------- LOCATION ---------- */}
      <Box sx={{ px: 0.5, mb: 1 }}>
        <Card sx={{ borderRadius: 3, border: `1px solid ${COLORS.border}` }}>
          <CardContent sx={{ p: 0.5 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, color: COLORS.textDark }}>
              Location Advantages
            </Typography>
            {d.locationAdvantages.length === 0 && <EmptyText>No location details</EmptyText>}
            {d.locationAdvantages.map((item, index, arr) => {
              const IconComp = resolveIcon(item.place_name);
              return (
                <Box
                  key={`${item.place_name}-${index}`}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    py: 1.5,
                    borderBottom: index < arr.length - 1 ? `1px solid ${COLORS.border}` : 'none',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconComp sx={{ fontSize: 20, color: COLORS.primary }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: COLORS.textDark, fontSize: 14, fontWeight: 600 }}>{item.place_name}</Typography>
                      <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>{item.distance}</Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ color: COLORS.blue, fontWeight: 600 }}>{item.travel_time}</Typography>
                </Box>
              );
            })}

            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined" fullWidth startIcon={<Map />}
                onClick={openGoogleMaps}
                disabled={!project.location && (project.lat === undefined || project.lng === undefined)}
                sx={{
                  borderRadius: 6, color: COLORS.primary, borderColor: COLORS.primary,
                  fontWeight: 700, py: 1,
                  '&:hover': { borderColor: COLORS.primaryDark, bgcolor: COLORS.primaryLight },
                }}
              >
                View on Google Maps
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* ---------- REQUEST CALLBACK ---------- */}
      <Box id="ads-request-callback" sx={{ px: 0.5 }}>
        <Card sx={{ borderRadius: 3, border: `1px solid ${COLORS.border}` }}>
          <CardContent sx={{ p: 0.5 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: COLORS.textDark }}>
              Request Callback
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                { label: 'Name', key: 'name', placeholder: 'Your full name' },
                { label: 'Phone', key: 'phone', placeholder: '+91 88765 43210' },
                { label: 'Email', key: 'email', placeholder: 'you@example.com' },
              ].map((f) => (
                <Box key={f.key}>
                  <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600 }}>{f.label}</Typography>
                  <TextField
                    placeholder={f.placeholder} fullWidth size="small"
                    value={contactForm[f.key]}
                    onChange={(e) => setContactForm({ ...contactForm, [f.key]: e.target.value })}
                    sx={{
                      mt: 0.5,
                      '& .MuiOutlinedInput-root': {
                        bgcolor: COLORS.inputBg, borderRadius: 2,
                        '& fieldset': { borderColor: COLORS.border },
                      },
                    }}
                  />
                </Box>
              ))}

              <Box>
                <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600 }}>Preferred Time</Typography>
                <TextField
                  select fullWidth size="small"
                  value={contactForm.preferredTime}
                  onChange={(e) => setContactForm((p) => ({ ...p, preferredTime: e.target.value }))}
                  sx={{
                    mt: 0.5,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: COLORS.inputBg, borderRadius: 2,
                      '& fieldset': { borderColor: COLORS.border },
                    },
                  }}
                >
                  <MenuItem value="morning">Morning (9-12)</MenuItem>
                  <MenuItem value="afternoon">Afternoon (12-3)</MenuItem>
                  <MenuItem value="evening">Evening (3-6)</MenuItem>
                </TextField>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600 }}>Message</Typography>
                <TextField
                  placeholder="Type your message..." multiline rows={3} fullWidth
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  sx={{
                    mt: 0.5,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: COLORS.inputBg, borderRadius: 2,
                      '& fieldset': { borderColor: COLORS.border },
                    },
                  }}
                />
              </Box>
            </Box>

            <Button
              variant="contained" fullWidth startIcon={<Phone />}
              onClick={handleContactSubmit}
              disabled={submitting || userLoading}
              sx={{
                mt: 1, borderRadius: 2, bgcolor: COLORS.primary,
                py: 1.1, fontWeight: 700, textTransform: 'none',
                '&:hover': { bgcolor: COLORS.primaryDark },
              }}
            >
              {submitting ? 'Submitting...' : 'Request Callback'}
            </Button>
          </CardContent>
        </Card>
      </Box>

    </Box>
  );
};

export default AddsPageInDetail;